import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'config/axios';
import { ReactQueryKeys } from 'config/constant';
import { refetchOnWindowFocus } from 'config/reactQuery';
import { useApiDefaults } from './useApiDefaults';

export function useGetRewardHistory(rewardId, enabled = true) {
  const queryClient = useQueryClient();
  const apiDefaults = useApiDefaults();

  const stableApiParams = useMemo(() => {
    const { PageNo, ...rest } = apiDefaults;
    return rest;
  }, [
    apiDefaults.RSAClientId,
    apiDefaults.ClientStoreId,
    apiDefaults.AppName,
    apiDefaults.member_number,
    apiDefaults.user_token,
    apiDefaults.Version,
    apiDefaults.DeviceType,
  ]);

  const body = useMemo(
    () => ({
      ...stableApiParams,
      reward_id: rewardId,
    }),
    [stableApiParams, rewardId]
  );

  const fetchPointsHistory = useCallback(async () => {
    const response = await axios.post('/AogGetPointsHistory', body);
    return response?.data?.data ?? [];
  }, [body]);

  const queryKey = [ReactQueryKeys.REWARD_POINTS_HISTORY, rewardId, stableApiParams];

  const {
    data: rewardHistory = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: fetchPointsHistory,
    enabled: enabled && Boolean(rewardId),
    refetchOnWindowFocus,
  });

  const prefetchRewardHistory = useCallback(async () => {
    if (!rewardId) return;
    const cached = queryClient.getQueryData(queryKey);
    if (cached !== undefined) return;
    await queryClient.prefetchQuery({
      queryKey,
      queryFn: fetchPointsHistory,
    });
  }, [queryClient, queryKey, rewardId, fetchPointsHistory]);

  return {
    rewardHistory,
    isLoading,
    error,
    prefetchRewardHistory,
  };
}
